import { toast } from 'sonner';
import type { UniticketReopenReport } from '@uniticket/shared';

// N40 (décisions du user du 07-10) — dire à l'écran ce qu'un geste a fait d'un ticket que Uniticket tenait pour
// résolu ou fermé. Un seul endroit, parce que deux écrans le disent : la fenêtre de validation (question, envoi
// chez Build, escalade, retour, verdict « en attente ») et le menu de reclassement.
//
// Le refus compte plus que le succès : un dossier remis en attente sur un ticket RESTÉ « Resolved » quitte toutes
// les vues de travail (c'est ce qui est arrivé à UT-2026-001663 le 05-10, sans un mot). D'où un avertissement qui
// reste à l'écran, et qui dit quoi faire.
//
// Les deux messages NOMMENT le ticket : après le geste, la fenêtre passe d'elle-même au ticket suivant (N43), et
// un message sans numéro se lirait comme parlant du dossier qui vient de s'ouvrir.

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Report = UniticketReopenReport | null | undefined;

/** La phrase de ce rapport — `null` quand le geste n'a pas porté sur un ticket résolu ou fermé. */
export function reopenSentence(reopen: Report, ticketNumber: string, t: Translate): string | null {
  if (!reopen) return null;
  // Le nom du statut tel que le bandeau du ticket l'affiche (« En cours », « Résolu »…).
  const status = t(`common:status.${reopen.status}`, { defaultValue: reopen.status });
  if (reopen.outcome === 'reopened') return t('tickets:validation.reopenDone', { ticket: ticketNumber, status });
  const message = t('tickets:validation.reopenFailed', { ticket: ticketNumber, status });
  return reopen.error ? `${message} (Uniticket : ${reopen.error})` : message;
}

/** Le ticket est RESTÉ résolu ou fermé : il y a quelque chose à faire sur ce dossier, on ne le quitte pas. */
export function reopenFailed(reopen: Report): boolean {
  return !!reopen && reopen.outcome !== 'reopened';
}

/** Le dire tout de suite, dans un message à lui. À appeler EN DERNIER : sonner met le plus récent devant. */
export function reportTicketReopen(reopen: Report, ticketNumber: string, t: Translate): void {
  const message = reopenSentence(reopen, ticketNumber, t);
  if (!message) return;
  if (reopenFailed(reopen)) toast.warning(message, { duration: 15000 });
  else toast.success(message);
}
